"use client";

import React from "react";
import { useFormContext } from "react-hook-form";
import { Loader, CircleCheck, Building2, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={`flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
export default function CorporatePartnershipInquiry() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="corporate-partnership-inquiry" ref={sectionRef} className="relative w-full py-24 bg-background overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-3xl" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[60%] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {}
          <div className={`space-y-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary-foreground text-sm font-medium">
                <Building2 className="size-4" />
                <span>Partner With Us</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-serif text-primary leading-tight">
                Align Your Brand with <span className="text-secondary">Purpose</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                Join a network of forward-thinking organizations dedicated to expanding economic opportunity. 
                Partnering with the COHCC Foundation connects your business with impactful programs in entrepreneurship, 
                education, and workforce development.
              </p>
            </div>

            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-primary font-serif">Why Partner?</h3>
              <ul className="space-y-4">
                {["Access to a diverse network of Hispanic entrepreneurs and leaders.", "Visibility at high-impact community events and digital platforms.", "Direct contribution to economic mobility and workforce innovation.", "Data-driven impact reporting for your CSR goals."].map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <div className="mt-1 bg-secondary/20 p-1 rounded-full">
                      <CircleCheck className="size-4 text-secondary-foreground" />
                    </div>
                    <span className="text-foreground/80">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="pt-4">
              <div className="p-6 bg-primary/5 rounded-xl border border-primary/10">
                <p className="text-primary font-medium italic">
                  "Our partnership with the COHCC Foundation has been instrumental in reaching new markets and supporting the local economy."
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                    JL
                  </div>
                  <div>
                    <p className="text-sm font-bold text-primary">Jorge Lopez</p>
                    <p className="text-xs text-muted-foreground">CEO, Denver Tech Solutions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={`bg-white rounded-2xl shadow-xl border border-border p-8 md:p-10 transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-primary font-serif">Corporate Partnership Inquiry</h3>
              <p className="text-muted-foreground mt-2">
                Fill out the form below to explore partnership opportunities. Our team will contact you shortly.
              </p>
            </div>

            <FormProvider formId="partnership_inquiry_form_partners" sectionName="partnership-inquiry" defaultValues={{
            name: "",
            organization: "",
            email: "",
            phone: "",
            partnership_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            organization: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            partnership_type: {
              required: true
            },
            message: {
              required: true
            }
          }} submitText="Submit Inquiry" successMessage="Thank you for your interest! We have received your inquiry and will be in touch soon." errorMessage="There was a problem submitting your inquiry. Please try again.">
              <PartnershipForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function PartnershipForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 bg-green-100 rounded-full flex items-center justify-center">
          <CircleCheck className="size-10 text-green-600" />
        </div>
        <div className="space-y-2">
          <h4 className="text-2xl font-bold text-primary">Inquiry Sent!</h4>
          <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="organization" rules={{
        required: "Organization name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane.doe@acme.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="partnership_type" rules={{
      required: "Please select a partnership type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Type of Partnership</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {["Sponsorship", "Strategic Alliance", "Employee Volunteering", "In-Kind Donation", "Other"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message / Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your interest and how you envision partnering with COHCC Foundation..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base bg-primary hover:bg-secondary hover:text-primary-foreground transition-colors duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Submitting...
          </> : <>
            Submit Inquiry <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}